import React from 'react';
import PropTypes from 'prop-types';

// A dealer's data source is stood up and registered by the data team — we only
// read it. Nothing here creates or provisions anything, so the vocabulary is
// deliberately about *connecting to* a source, never about building one.
export const TENANT_STATUS_LABEL = {
    provisioning: 'Source pending',
    waiting_for_data: 'Waiting for data',
    active: 'Active',
    suspended: 'Suspended',
};

export const TENANT_STATUS_HINT = {
    provisioning: 'The data team has not finished registering this source yet.',
    waiting_for_data: 'We can reach the source, but it has not delivered data yet.',
    active: 'Connected and serving this dealership.',
    suspended: 'Connected, but switched off for this dealership.',
};

// Turn a machine step key ("read_inventory") into something an admin can read.
export const humanizeStep = (step) => String(step || '')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

export const hasFailingStep = (steps) => (steps || []).some((s) => s.status === 'fail');
export const hasWarningStep = (steps) => (steps || []).some((s) => s.status === 'warn');

// A refused activation answers 409 with detail = { message, steps }, while every other
// admin route answers with a plain string detail. One reader for both shapes.
export const sourceError = (error, fallback) => {
    const detail = error?.details?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (detail?.message) return detail.message;
    return error?.message || fallback;
};

// The failing steps a refused activation came back with, if any.
export const failedSteps = (error) => {
    const steps = error?.details?.detail?.steps;
    return Array.isArray(steps) && steps.length ? steps : null;
};

export function TenantStatusBadge({ status }) {
    return (
        <span className={`ac-status ac-ds-badge ac-ds-badge-${status}`} title={TENANT_STATUS_HINT[status] || ''}>
            {TENANT_STATUS_LABEL[status] || status}
        </span>
    );
}

TenantStatusBadge.propTypes = {
    status: PropTypes.string,
};

// Per-step results of a connection check. Green = fine, amber = readable but
// incomplete (the dependent panels simply stay empty), red = blocks activation.
function DataSourceCheck({ result, compact }) {
    if (!result) return null;

    const steps = result.steps || [];
    const failed = hasFailingStep(steps);
    const warned = hasWarningStep(steps);

    return (
        <div className={`ac-ds-check ${compact ? 'ac-ds-check-compact' : ''}`}>
            <ul className="ac-ds-steps">
                {steps.map((s, i) => (
                    <li key={`${s.step}-${i}`} className={`ac-ds-step ac-ds-step-${s.status}`}>
                        <span className="ac-ds-dot" aria-hidden="true" />
                        <span className="ac-ds-step-body">
                            <span className="ac-ds-step-name">{humanizeStep(s.step)}</span>
                            {s.detail && <span className="ac-ds-step-detail">{s.detail}</span>}
                        </span>
                        <span className="ac-ds-step-tag">
                            {s.status === 'ok' ? 'OK' : s.status === 'warn' ? 'Partial' : 'Failed'}
                        </span>
                    </li>
                ))}
                {steps.length === 0 && <li className="ac-muted">The check returned no steps.</li>}
            </ul>

            {failed && (
                <p className="ac-ds-verdict ac-ds-verdict-fail">
                    This source cannot be activated until the failing checks above are resolved.
                    Send the detail text back to the data team — they own the source.
                </p>
            )}
            {!failed && warned && (
                <p className="ac-ds-verdict ac-ds-verdict-warn">
                    Good enough to activate. The amber items are datasets we could not read,
                    so the panels that depend on them will stay empty until the data lands.
                </p>
            )}
            {!failed && !warned && steps.length > 0 && (
                <p className="ac-ds-verdict ac-ds-verdict-ok">
                    Everything we need is readable. This source is ready to activate.
                </p>
            )}
        </div>
    );
}

DataSourceCheck.propTypes = {
    result: PropTypes.shape({
        ok: PropTypes.bool,
        steps: PropTypes.arrayOf(PropTypes.shape({
            step: PropTypes.string,
            status: PropTypes.string,
            detail: PropTypes.string,
        })),
    }),
    compact: PropTypes.bool,
};

export default DataSourceCheck;
